export const DEFAULT_6529_API_BASE_URL = "https://api.6529.io/api";
export const DEFAULT_MAINNET_RPC_URL = "https://rpc1.6529.io";
export const DEFAULT_SAFE_TX_SERVICE_BASE_URL = "https://api.safe.global/tx-service/eth/api/v1";
export const DEFAULT_SAFE_PARENT_ORIGINS = Object.freeze(["https://app.safe.global"]);

export type RuntimeBuildMetadata = Readonly<{
  canonicalOrigin?: string | undefined;
  sourceRepoUrl?: string | undefined;
  appVersion?: string | undefined;
  buildCommit?: string | undefined;
  buildReleaseId?: string | undefined;
  buildTimestamp?: string | undefined;
}>;

export type RuntimeEndpointConfig = Readonly<{
  api6529BaseUrl: string;
  safeTxServiceBaseUrl: string;
  mainnetRpcUrl?: string | undefined;
}>;

export type RuntimeSafeConfig = Readonly<{
  parentOriginAllowlist: readonly string[];
  approvedModules: ReadonlySet<string>;
  approvedGuards: ReadonlySet<string>;
  approvedFallbackHandlers: ReadonlySet<string>;
}>;

export type RuntimeConfigSnapshot = Readonly<{
  endpoints: RuntimeEndpointConfig;
  safe: RuntimeSafeConfig;
  build: RuntimeBuildMetadata;
}>;

export type RuntimeConfigInput = Partial<{
  api6529BaseUrl: string | null;
  safeTxServiceBaseUrl: string | null;
  mainnetRpcUrl: string | null;
  safeParentOrigins: string;
  safeAllowedParentOrigins: string;
  approvedModules: string;
  approvedGuards: string;
  approvedFallbackHandlers: string;
  buildMetadata: Partial<RuntimeBuildMetadata>;
}>;

export function toTrimmedString(value: string | undefined): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function normalizeRuntimeOrigin(value: string | undefined): string | undefined {
  const trimmed = toTrimmedString(value);
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return undefined;
    return parsed.origin;
  } catch {
    return undefined;
  }
}

export function normalizeHttpsOrigin(value: string | undefined): string | undefined {
  const origin = normalizeRuntimeOrigin(value);
  if (!origin) return undefined;

  try {
    const parsed = new URL(origin);
    return parsed.protocol === "https:" ? parsed.origin : undefined;
  } catch {
    return undefined;
  }
}

function isLocalHttpHostname(hostname: string): boolean {
  const normalized = hostname.toLowerCase();
  return (
    normalized === "localhost" ||
    normalized === "0.0.0.0" ||
    normalized === "::1" ||
    normalized === "[::1]" ||
    normalized.startsWith("127.")
  );
}

function normalizeRuntimeEndpointBaseUrl(value: string | undefined): string | undefined {
  const trimmed = toTrimmedString(value);
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return undefined;
    if (parsed.protocol === "http:" && !isLocalHttpHostname(parsed.hostname)) return undefined;
    if (parsed.username || parsed.password || parsed.search || parsed.hash) return undefined;

    const normalized = parsed.toString().replace(/\/+$/u, "");
    return normalized || undefined;
  } catch {
    return undefined;
  }
}

export function normalizeRuntimeBaseUrl(raw: string | null | undefined, fallback: string): string {
  const trimmedFallback = fallback.replace(/\/+$/u, "");
  return (
    (normalizeRuntimeEndpointBaseUrl(raw ?? undefined) ??
      normalizeRuntimeEndpointBaseUrl(fallback) ??
      trimmedFallback) ||
    fallback
  );
}

export function resolve6529ApiBaseUrl(raw?: string | null): string {
  return normalizeRuntimeBaseUrl(
    raw ?? (import.meta.env.VITE_6529_API_BASE_URL as string | undefined),
    DEFAULT_6529_API_BASE_URL
  );
}

export function normalizeSafeTxServiceBaseUrl(raw?: string | null): string {
  return normalizeRuntimeBaseUrl(raw, DEFAULT_SAFE_TX_SERVICE_BASE_URL);
}

export function resolveSafeTxServiceBaseUrl(raw?: string | null): string {
  return normalizeSafeTxServiceBaseUrl(
    raw ?? (import.meta.env.VITE_SAFE_TX_SERVICE_BASE_URL as string | undefined)
  );
}

export function resolveMainnetRpcUrl(raw?: string | null): string | undefined {
  return (
    normalizeRuntimeEndpointBaseUrl(
      raw ?? (import.meta.env.VITE_MAINNET_RPC_URL as string | undefined)
    ) ?? normalizeRuntimeEndpointBaseUrl(DEFAULT_MAINNET_RPC_URL)
  );
}

export function parseRuntimeOriginList(value: string | undefined): string[] {
  if (typeof value !== "string") return [];
  return value
    .split(/[,\s]+/u)
    .map((entry) => normalizeRuntimeOrigin(entry))
    .filter((entry): entry is string => typeof entry === "string");
}

export function resolveSafeParentOriginAllowlist(rawValue?: string): string[] {
  const parsedOrigins = parseRuntimeOriginList(
    rawValue ??
      (import.meta.env.VITE_SAFE_PARENT_ORIGINS as string | undefined) ??
      (import.meta.env.VITE_SAFE_ALLOWED_PARENT_ORIGINS as string | undefined)
  );
  return Array.from(new Set([...DEFAULT_SAFE_PARENT_ORIGINS, ...parsedOrigins]));
}

export function normalizeAddress(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!/^0x[a-fA-F0-9]{40}$/u.test(trimmed)) return undefined;
  return trimmed.toLowerCase();
}

export function parseAddressAllowlist(value: string | undefined): Set<string> {
  if (typeof value !== "string") return new Set();
  const parsed = value
    .split(/[,\s]+/u)
    .map((entry) => normalizeAddress(entry))
    .filter((entry): entry is string => typeof entry === "string");
  return new Set(parsed);
}

export function resolveApprovedModules(raw?: string): Set<string> {
  return parseAddressAllowlist(
    raw ?? (import.meta.env.VITE_SAFE_APPROVED_MODULES as string | undefined)
  );
}

export function resolveApprovedGuards(raw?: string): Set<string> {
  return parseAddressAllowlist(
    raw ?? (import.meta.env.VITE_SAFE_APPROVED_GUARDS as string | undefined)
  );
}

export function resolveApprovedFallbackHandlers(raw?: string): Set<string> {
  return parseAddressAllowlist(
    raw ?? (import.meta.env.VITE_SAFE_APPROVED_FALLBACK_HANDLERS as string | undefined)
  );
}

export function resolveRuntimeConfig(input: RuntimeConfigInput = {}): RuntimeConfigSnapshot {
  const mainnetRpcUrl = resolveMainnetRpcUrl(input.mainnetRpcUrl);
  const endpoints = Object.freeze({
    api6529BaseUrl: resolve6529ApiBaseUrl(input.api6529BaseUrl),
    safeTxServiceBaseUrl: resolveSafeTxServiceBaseUrl(input.safeTxServiceBaseUrl),
    ...(mainnetRpcUrl ? { mainnetRpcUrl } : {})
  });
  const safeParentOrigins =
    input.safeParentOrigins ??
    input.safeAllowedParentOrigins ??
    (import.meta.env.VITE_SAFE_PARENT_ORIGINS as string | undefined) ??
    (import.meta.env.VITE_SAFE_ALLOWED_PARENT_ORIGINS as string | undefined);
  const safe = Object.freeze({
    parentOriginAllowlist: Object.freeze(resolveSafeParentOriginAllowlist(safeParentOrigins)),
    approvedModules: resolveApprovedModules(input.approvedModules),
    approvedGuards: resolveApprovedGuards(input.approvedGuards),
    approvedFallbackHandlers: resolveApprovedFallbackHandlers(input.approvedFallbackHandlers)
  });
  const build = Object.freeze({
    ...resolveRuntimeBuildMetadata(),
    ...input.buildMetadata
  });

  return Object.freeze({
    endpoints,
    safe,
    build
  });
}

export function isValidCommitSha(value: string | undefined): boolean {
  return typeof value === "string" && /^[0-9a-f]{7,40}$/iu.test(value);
}

export function isValidBuildReleaseId(value: string | undefined): boolean {
  return typeof value === "string" && /^[A-Za-z0-9._:-]{6,128}$/u.test(value);
}

export function isValidIsoTimestamp(value: string | undefined): boolean {
  if (typeof value !== "string") return false;
  if (!/T/u.test(value) || !/(Z|[+-]\d{2}:\d{2})$/u.test(value)) return false;
  return Number.isFinite(Date.parse(value));
}

export function isValidSourceRepoUrl(value: string | undefined): boolean {
  if (typeof value !== "string") return false;

  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function resolveRuntimeBuildMetadata(): RuntimeBuildMetadata {
  return Object.freeze({
    canonicalOrigin: typeof __CANONICAL_ORIGIN__ === "undefined" ? undefined : __CANONICAL_ORIGIN__,
    sourceRepoUrl: typeof __SOURCE_REPO_URL__ === "undefined" ? undefined : __SOURCE_REPO_URL__,
    appVersion: typeof __APP_VERSION__ === "undefined" ? undefined : __APP_VERSION__,
    buildCommit: typeof __BUILD_COMMIT__ === "undefined" ? undefined : __BUILD_COMMIT__,
    buildReleaseId: typeof __BUILD_RELEASE_ID__ === "undefined" ? undefined : __BUILD_RELEASE_ID__,
    buildTimestamp: typeof __BUILD_TIMESTAMP__ === "undefined" ? undefined : __BUILD_TIMESTAMP__
  });
}
